import type { Textos } from '../index';

/**
 * Los mismos textos del panel, en español. Las claves son las de ../en y TypeScript no deja
 * que falte ni sobre ninguna.
 */
const es: Textos = {
  comun: {
    cargando: 'Un momento…',
    volverAIntentar: 'Volver a intentar',
    guardar: 'Guardar',
    guardando: 'Guardando…',
    cancelar: 'Cancelar',
    cerrar: 'Cerrar',
    editar: 'Editar',
    elegir: 'Elegir…',
    nombre: 'Nombre',
    notas: 'Notas',
    contacto: 'Contacto',
    organizacion: 'Organización',
    organizaciones: 'Organizaciones',
    appliance: 'Appliance',
    appliances: 'Appliances',
    version: 'Versión',
    estado: 'Estado',
    equipos: 'Equipos',
    disco: 'Disco',
    ultimoContacto: 'Último contacto',
    canal: 'canal',
    buscar: 'Buscar',
    sinDatos: 'Sin datos',
    noReportada: 'no la reportó',
    errorRed: 'No se pudo llegar a la API del Centro. Revise la conexión.',
    errorSinMotivo: (estado: number) => `La API contestó ${estado} sin explicar por qué.`,
    errorRaro: 'Pasó algo que no esperábamos. Vuelva a intentar.',
    hace: (cuanto: string) => `hace ${cuanto}`,
    abriendo: 'Abriendo el Centro de control…',
    canales: { estable: 'estable', beta: 'beta' },
    roles: { admin: 'Administración', soporte: 'Soporte', ventas: 'Ventas' },
  },

  acciones: {
    'sesion.ingreso': 'entró',
    'appliance.crear': 'creó un appliance',
    'appliance.cambiar': 'cambió un appliance',
    'appliance.borrar': 'borró un appliance',
    'appliance.codigo': 'emitió un código de alta',
    'appliance.actualizar': 'pidió una actualización',
    'organizacion.crear': 'creó una organización',
    'organizacion.cambiar': 'cambió una organización',
    'licencia.emitir': 'emitió una licencia',
    'licencia.reenviar': 'volvió a mandar una licencia',
    'licencia.revocar': 'revocó una licencia',
    'version.publicar': 'publicó una versión',
    'version.retirar': 'retiró una versión',
    'version.actualizar-canal': 'repartió una versión',
    'orden.cancelar': 'canceló una orden',
    'sesion.fallida': 'no pudo entrar',
    'centro.consentimiento': 'cambió el consentimiento',
    'usuario.cambiar': 'cambió un usuario',
    'usuario.crear': 'creó un usuario',
  },

  ajustes: {
    nav: 'Secciones de ajustes',
    grupos: { acceso: 'Clientes y acceso', actualizaciones: 'Actualizaciones', avisos: 'Avisos' },
    secciones: {
      organizaciones: 'Organizaciones',
      usuarios: 'Usuarios',
      claves: 'Claves de API',
      bitacora: 'Bitácora',
      versiones: 'Versiones',
      correo: 'Servidor de correo',
      avisos: 'Mis avisos',
    },
    titulo: 'Ajustes',
    que: 'Por dónde nos llegan los avisos del Centro, y qué quiere saber cada quien.',
    correo: 'Servidor de correo',
    correoQue:
      'De acá salen los avisos del Centro. Es su propio servidor: no tiene nada que ver con el que usa un appliance para avisarle a su cliente.',
    modos: { smtp: 'Servidor SMTP', m365: 'Microsoft 365' },
    remitente: 'Manda como',
    remitenteEjemplo: 'centro@kuantabridge.com',
    servidor: 'Servidor',
    servidorEjemplo: 'smtp.office365.com',
    puerto: 'Puerto',
    tlsDirecto: 'TLS directo (puerto 465). Si no, STARTTLS cuando el servidor lo ofrece.',
    usuario: 'Usuario',
    tenant: 'Tenant',
    app: 'Id de la aplicación',
    clave: 'Contraseña',
    secreto: 'Secreto de la aplicación',
    yaGuardada: 'ya guardada',
    vacioLaDeja: 'Vacío deja la que ya está guardada.',
    noSeMuestra: 'Queda en el servidor y no se vuelve a mostrar.',
    faltaRemitente: 'Escriba la dirección desde la que manda.',
    faltaHost: 'Escriba la dirección del servidor.',
    faltaTenant: 'Escriba el tenant.',
    faltaApp: 'Escriba el id de la aplicación.',
    correoGuardado: 'Servidor de correo guardado.',
    probar: 'Mandar uno de prueba',
    paraLaPrueba: 'Mandar la prueba a',
    paraInvalido: 'Escriba la dirección a la que quiere la prueba.',
    pruebaMandada: (a: string) => `Se mandó la prueba a ${a}.`,
    misAvisos: 'Qué quiero que me avisen',
    misAvisosQue: 'Cada aviso se manda una vez cuando empieza y otra cuando se resuelve. Nada se repite mientras sigue pasando.',
    idiomaDelCorreo: 'Idioma de mis correos',
    suscripcionGuardada: 'Guardado: eso es lo que le va a llegar.',
    porOmision: 'Esto es lo que recibe su rol por omisión.',
  },

  avisos: {
    sin_contacto: 'Un appliance deja de contestar',
    disco: 'Se le está llenando el disco',
    motor_caido: 'Se le cayó un motor',
    licencia_por_vencer: 'Una licencia está por vencer',
    licencia_vencida: 'Una licencia venció',
    version_vieja: 'Un appliance quedó atrás de versión',
    actualizacion_fallida: 'Falló una actualización',
    entregas_fallidas: 'Las alertas de un cliente no salen',
  },

  soporte: {
    titulo: 'Sesión de soporte',
    estados: { ninguna: 'ninguna', pedida: 'esperando al cliente', abierta: 'abierta' },
    que: 'Llegar a sus equipos cubre lo que monitorean. Para llegar a otra cosa de su red —un servidor, algo que nadie registró— se pide una sesión y el cliente la aprueba.',
    motivo: 'Para qué',
    motivoEjemplo: 'Revisar el servidor de correo que dejó de contestar',
    motivoAyuda: 'Esto lo lee el cliente: diga qué va a hacer, no «soporte».',
    loPidio: 'La pidió',
    aprobo: 'La aprobó',
    pedida: 'Pedida',
    hasta: 'Hasta',
    quedan: (min: number) => (min >= 60 ? `quedan ${Math.floor(min / 60)} h ${min % 60} min` : `quedan ${min} min`),
    esperando: 'El cliente la tiene que aprobar en su propio KNOC360, en Ajustes → Centro de control.',
    pedirla: 'Pedir una sesión',
    pedirBoton: 'Pedírsela al cliente',
    pedirQue: 'Le llega al cliente al instante. Hasta que él la apruebe, no se abre nada.',
    porCuanto: 'Por cuánto tiempo',
    horas: (h: number) => (h === 1 ? '1 hora' : `${h} horas`),
    faltaMotivo: 'Diga para qué es: lo lee el cliente.',
    pedidaOk: 'Pedida. Ahora decide el cliente.',
    cancelar: 'Cancelarla',
    cancelada: 'Cancelada.',
    abrirPuente: 'Abrir un puente',
    abrirBoton: 'Darme el enlace',
    puenteQue: 'Un puerto en su propia PC que sale adentro de la red del cliente. Su programa se conecta a 127.0.0.1 y el abridor hace el resto.',
    destino: 'Dirección en su red',
    puerto: 'Puerto',
    faltaDestino: 'Diga a qué llegar.',
    porCualMotor: 'Por cuál motor',
    motorSolo: 'El que monitorea esa red',
    motorAyuda: 'Solo hace falta cuando el cliente tiene más de uno y la dirección no es de sus equipos.',
    listo: 'Listo.',
    listoQue: 'Ábralo con el abridor de KNOC360 instalado en esta PC:',
    abrirlo: 'Abrirlo',
    sinContacto: 'El appliance está sin contacto: no se le puede pedir ahora.',
  },

  codigo: {
    para: (nombre: string) => `Código de alta de ${nombre}`,
    titulo: 'Código de alta',
    como: 'De un solo uso, vence en 7 días. El appliance lo manda una vez, mientras se instala, para quedar registrado.',
    que: 'Cópielo ahora y páselo a quien instala el appliance. Si se pierde no se recupera: hay que pedir uno nuevo desde el detalle del appliance.',
    unaSolaVez: 'Este código no se vuelve a mostrar.',
    guardado: 'Ya lo guardé',
  },

  marco: {
    pantallas: 'Pantallas del Centro de control',
    tablero: 'Tablero',
    appliances: 'Appliances',
    red: 'Red',
    ajustes: 'Ajustes',
    salir: 'Salir',
    idioma: 'Idioma',
  },

  ingreso: {
    marca: '· Centro de control',
    lema: 'Todos los appliances,\nen una pantalla.',
    que: 'Todos los appliances instalados: quién está en línea, con qué versión, qué tan lleno tiene el disco y a quién se le vence la licencia. Este es el panel que usa Kuanta Bridge adentro, no el que ve un cliente.',
    titulo: 'Entre a su cuenta',
    interno: 'Centro de control de KNOC360 · uso interno',
    vencida: 'La sesión venció. Vuelva a entrar para seguir donde estaba.',
    correo: 'Correo',
    correoEjemplo: 'usted@kuantabridge.com',
    clave: 'Contraseña',
    entrando: 'Entrando…',
    entrar: 'Entrar',
    olvide: '¿Olvidó la contraseña?',
    olvideTitulo: 'Restablecer la contraseña',
    olvideQue: 'Escriba el correo de su cuenta y le mandamos un enlace para elegir una contraseña nueva.',
    mandarEnlace: 'Mandar el enlace',
    mandando: 'Mandando…',
    revise: 'Revise su correo',
    enviado: (c: string) =>
      `Si ${c} tiene cuenta en el Centro, en un par de minutos le llega un enlace. Sirve una sola vez y vence en 30 minutos. Si no llega, revise el correo no deseado o pídale a un administrador que la restablezca.`,
    volverAEntrar: 'Volver a entrar',
    nuevaTitulo: 'Elija una contraseña nueva',
    nuevaQue: 'Al guardarla se cierran las sesiones abiertas con la anterior.',
    nueva: 'Contraseña nueva',
    repetir: 'Repítala',
    corta: 'La contraseña tiene que tener al menos 10 caracteres.',
    noCoinciden: 'Las dos contraseñas no son iguales.',
    guardando: 'Guardando…',
    guardarClave: 'Guardar la contraseña',
    claveLista: 'Contraseña cambiada',
    claveListaQue: 'Ya puede entrar con la contraseña nueva.',
    enlaceMuerto: 'Ese enlace ya no sirve',
    enlaceMuertoQue: 'Venció o ya se usó. Pida uno nuevo desde la pantalla de ingreso.',
    pie: 'Centro de control · Kuanta Bridge',
  },

  tablero: {
    titulo: 'Tablero',
    que: 'Los appliances que Kuanta Bridge tiene instalados: qué necesita a alguien hoy, y qué está andando bien.',
    alDia: (hora: string) => `al día a las ${hora}`,
    applianceEnLinea: 'Appliances en línea',
    sinContactoN: (n: number) => `${n} sin contacto`,
    esperandoAltaN: (n: number) => `${n} esperando el alta`,
    suspendidosN: (n: number) => `${n} suspendidos`,
    equiposMonitoreados: 'Equipos monitoreados',
    equiposCaidosN: (n: string) => `${n} caídos ahora`,
    todosArriba: 'todos contestando',
    licenciasActivas: 'Licencias vigentes',
    porVencerN: (n: number) => `${n} por vencer`,
    vencidasN: (n: number) => `${n} vencidas`,
    sinLicenciaN: (n: number) => `${n} sin licencia`,
    todasAlDia: 'nada por renovar',
    ultimaEstable: 'Última versión estable',
    atrasadosN: (n: number) => `${n} appliance(s) atrás`,
    todosAlDiaVersion: 'todos la tienen',
    sinVersiones: 'todavía no se publicó una estable',
    radar: 'Los appliances, en vivo',
    radarQue: 'Cada eco es un appliance: mientras más lejos del centro, más hace que no late. El color es cómo está; cada tajada es un cliente.',
    paraMirar: 'Necesita a alguien hoy',
    todoEnOrden: 'Nada que mirar',
    todoEnOrdenQue: (appliances: string, equipos: string) =>
      `${appliances} appliance(s) en línea, con disco sano y sus motores conectados, cuidando ${equipos} equipos.`,
    renovaciones: 'Renovaciones que vienen',
    sinRenovaciones: 'Ninguna licencia vence en los próximos 60 días.',
    enDias: (d: number) => (d === 0 ? 'hoy' : `en ${d} d`),
    vencidaHace: (d: number) => `hace ${d} d`,
    porVersion: 'Appliances por versión',
    sinVersion: 'Ningún appliance reportó su versión todavía.',
    clientes: 'Clientes',
    sinClientes: 'Ninguna organización tiene appliances todavía.',
    salud: 'Equipos arriba',
    compartida: 'red compartida',
    actividad: 'Última actividad',
    sinActividad: 'Todavía no hay nada anotado.',
  },

  consola: {
    titulo: (nombre: string) => `Consola de ${nombre}`,
    que: 'SSH al propio appliance, por su canal con el Centro de control: sin VPN y sin abrir ningún puerto en el cliente. Como soporte entra con la llave del Centro; los appliances de antes piden su clave, que no se guarda.',
    conLlave: 'Vacía: llave del Centro',
    usuario: 'Usuario',
    clave: 'Clave',
    entrar: 'Conectar',
    fallo: 'No se pudo abrir la consola.',
    estados: {
      datos: '',
      conectando: 'Llegando al appliance…',
      entrando: 'Entrando…',
      abierta: 'Conectada',
      cerrada: 'Cerrada',
    },
    otraVez: 'Volver a conectar',
    cerrar: 'Desconectar',
    sesionCerrada: 'La sesión se cerró.',
  },
  libres: {
    titulo: 'Esperando organización',
    que: 'Estos appliances se registraron solos apenas tuvieron internet. Reconozca cada uno por la huella que muestra su pantalla y asígnelo a una organización.',
    huella: 'Huella',
    ip: 'Se conecta desde',
    version: 'Versión',
    visto: 'Visto',
    enLinea: 'En línea',
    sinContacto: 'Sin contacto',
    consola: 'Consola',
    asignar: 'Asignar',
    asignarTitulo: (nombre: string) => `Asignar ${nombre}`,
    asignarQue: 'Desde ya es un appliance más de esa organización: lo muestra en su pantalla y en su web.',
    nombreAyuda: 'Cómo se va a ver en el Centro de control.',
    descartar: 'Descartar',
    descartarTitulo: (nombre: string) => `¿Descartar ${nombre}?`,
    descartarQue:
      'Se desconecta y con esta llave no puede volver a registrarse solo. Es para una prueba o una VM clonada, no para el appliance de un cliente.',
    asignado: (nombre: string, organizacion: string) => `${nombre} ahora es de ${organizacion}.`,
    descartado: (nombre: string) => `${nombre} quedó descartado.`,
    faltaOrganizacion: 'Elija la organización.',
  },
  appliances: {
    titulo: 'Appliances',
    que: 'Todo lo que Kuanta Bridge tiene instalado. El Centro sabe cómo está cada uno, nunca qué monitorea.',
    nuevo: 'Appliance nuevo',
    crearYCodigo: 'Crear y darme el código',
    elijaOrganizacion: 'Elija la organización que lo compró.',
    faltaNombre: 'Póngale un nombre al appliance.',
    sinOrganizaciones: 'Todavía no hay organizaciones: cree una primero.',
    nombreEjemplo: 'Appliance de Hotel Norte',
    nombreAyuda: 'Así se ve acá y en su propia web.',
    notasOpcional: 'Notas (opcional)',
    notasEjemplo: 'Dónde está, quién lo instaló, lo que valga la pena saber.',
    todasLasOrganizaciones: 'Todas las organizaciones',
    estado: 'Estado',
    todosLosEstados: 'Todos los estados',
    activo: 'Activo',
    esperandoAlta: 'Esperando el alta',
    suspendido: 'Suspendido',
    revocado: 'Revocado',
    buscarEjemplo: 'Buscar por nombre u organización',
    nadaConEsoFiltros: 'Ningún appliance coincide con lo que buscó. Pruebe con menos filtros.',
    ningunoTodavia: 'Todavía no hay appliances en el Centro. Cree el primero con «Appliance nuevo».',
    caidos: 'Caídos',
    creado: (nombre: string) => `Se creó ${nombre}.`,
  },

  appliance: {
    volver: '← Volver a appliances',
    detalles: 'Detalle',
    abrirSuApp: 'Abrir su KNOC360',
    codigoNuevo: 'Código nuevo',
    activar: 'Activar',
    suspender: 'Suspender',
    revocar: 'Revocar',
    borrar: 'Borrar',
    sistema: 'Sistema operativo',
    prendidoDesde: 'Prendido desde',
    noReportadaTodavia: 'todavía no la reportó',
    nuncaConecto: 'nunca conectó',
    pestanas: 'Secciones del appliance',
    pestana: {
      salud: 'Salud',
      actualizaciones: 'Actualizaciones',
      internet: 'Acceso desde internet',
      licencia: 'Licencia',
      soporte: 'Soporte',
      consola: 'Consola',
      detalles: 'Detalles',
    },
    canalEstableQue: 'Estable: solo le ofrecemos versiones ya probadas. Es lo que conviene para un cliente en producción.',
    canalBetaQue: 'Beta: recibe las versiones nuevas primero, para probarlas antes de pasarlas a estable. Para appliances de prueba o de confianza.',
    canalDeVersiones: 'Canal de versiones',
    canalCambiado: 'Se cambió el canal de versiones.',
    creadoEnElCentro: 'Creado en el Centro',
    notasGuardadas: 'Notas guardadas.',
    sinNotas: 'Sin notas.',
    direccionInternet: 'Entrar desde internet',
    sinNombreInternet: 'Sin nombre: su gente solo entra desde adentro de su red.',
    nombreInternet: 'Nombre',
    nombreEjemplo: 'ej. ews',
    conNombre: 'Con nombre',
    sinNombre: 'Sin nombre',
    guardarNombre: 'Guardar',
    quitarNombre: 'Quitar',
    nombreGuardado:
      'Nombre guardado. El appliance ya sabe su dirección; su administrador tiene que encender la entrada desde internet en su KNOC360.',
    nombreQuitado: 'Se quitó el nombre: ya no se entra desde internet.',
    nombreAyuda: 'Un nombre por appliance: el login decide qué cliente ve cada uno. El DNS y el certificado del nombre van aparte.',
    nombresApagados: 'Este Centro no tiene configurado el dominio de los nombres.',
    descartar: 'Descartar',
    guardarNotas: 'Guardar notas',
    saludAhora: 'Salud ahora',
    noSeDioDeAlta: 'Este appliance todavía no se dio de alta: nunca reportó salud.',
    sinSalud: 'Todavía no llegó salud de este appliance.',
    cpu: 'CPU',
    memoria: 'Memoria',
    libres: (gb: string) => `${gb} GB libres`,
    base: 'Base',
    prendidoHace: (cuanto: string) => `prendido hace ${cuanto}`,
    equiposCaidos: (n: string) => `${n} caídos`,
    motores: 'Motores',
    conectados: 'conectados',
    licenciaVence: 'vence',
    licenciaTope: 'tope',
    licenciaEquipos: 'equipos',
    ultimas24: 'Últimas 24 horas',
    ultimosErrores: 'Últimos errores que reportó',
    sinErrores: 'No reportó errores en el último día.',
    activado: (nombre: string) => `${nombre} vuelve a estar activo.`,
    suspenderA: (nombre: string) => `¿Suspender ${nombre}?`,
    suspendido: (nombre: string) => `${nombre} quedó suspendido.`,
    suspenderQue:
      'Queda marcado como suspendido acá y deja de contar como activo. El appliance sigue monitoreando y alertando en la red del cliente: esto no lo apaga. Se puede volver a activar cuando quiera.',
    revocarA: (nombre: string) => `¿Revocar la credencial de ${nombre}?`,
    revocarBoton: 'Revocar la credencial',
    revocado: (nombre: string) => `Se revocó la credencial de ${nombre}.`,
    revocarCorta: 'Se le corta el canal de control.',
    revocarQue: 'La conexión se cae ya mismo y no vuelve a abrir: el Centro deja de recibir su salud y no le puede mandar más órdenes ni licencias.',
    revocarSigue:
      'El appliance sigue monitoreando, alertando y respaldando en la red del cliente, y avisa en su propia web que perdió contacto con el Centro. Para recuperarlo hay que darlo de alta otra vez con un código nuevo.',
    borrarA: (nombre: string) => `¿Borrar ${nombre} del Centro?`,
    borrarQue:
      'Este appliance nunca conectó, así que se puede borrar sin dejar nada colgando: se va con su código de alta. Si ya lo instalaron en algún lado, cancele y revóquelo en vez de borrarlo.',
  },

  licencia: {
    titulo: 'Licencia',
    sinLicencia: 'Este appliance todavía no tiene licencia. Sigue funcionando, pero nadie la está pagando.',
    plan: 'Plan',
    planes: { basico: 'Básico', pro: 'Pro', ilimitado: 'Ilimitado' },
    sinTope: 'sin tope de equipos',
    equiposCortos: 'equipos',
    equipos: (n: string) => `${n} equipos`,
    vence: 'Vence',
    emitida: 'Emitida',
    entregada: 'Entregada al appliance',
    todaviaNo: 'todavía no',
    esperandoAlAppliance: 'esperando: el appliance está apagado',
    venceHoy: 'vence hoy',
    faltan: (d: number) => (d === 1 ? 'falta 1 día' : `faltan ${d} días`),
    vencioHace: (d: number) => (d === 1 ? 'venció hace 1 día' : `venció hace ${d} días`),
    emitir: 'Emitir licencia',
    renovar: 'Renovar',
    reenviar: 'Volver a mandar',
    bajarArchivo: 'Bajar el archivo',
    revocar: 'Revocar',
    mandada: 'Se le mandó la licencia al appliance.',
    mandadaDespues: 'El appliance está apagado: la recibe cuando conecte.',
    anteriores: 'Licencias anteriores',
    hasta: 'Vence',
    emitidaPor: 'La emitió',
    reemplazada: 'reemplazada',
    revocadaPor: (motivo: string) => `revocada: ${motivo}`,
    renovarTitulo: 'Renovar la licencia',
    faltaEquipos: 'Diga cuántos equipos cubre la licencia.',
    faltaVence: 'Diga cuándo vence.',
    emitidaOk: 'Licencia emitida.',
    ilimitado: 'Ilimitado (sin tope de equipos)',
    equiposQueCubre: 'Equipos que cubre',
    venceEl: 'Vence el',
    queImplica:
      'Pasarse del tope frena los equipos nuevos; los que ya están se siguen midiendo. Vencida hay 30 días más y después el appliance queda en solo lectura — el monitoreo nunca se apaga.',
    revocarTitulo: 'Revocar esta licencia',
    faltaMotivo: 'Diga por qué: queda en la bitácora.',
    revocada: 'Licencia revocada.',
    revocarQue: 'El appliance vuelve a la licencia que tenía antes de esta, o a ninguna. Sigue monitoreando igual.',
    porQue: 'Por qué',
    porQueEjemplo: 'Se emitió por error, se canceló el contrato…',
    estados: {
      vigente: 'Licencia vigente',
      por_vencer: 'Licencia por vencer',
      gracia: 'Licencia en gracia',
      solo_lectura: 'Licencia vencida (solo lectura)',
      sin_licencia: 'Sin licencia',
    },
  },

  actualizar: {
    hayNueva: (v: string) => `Hay una versión nueva: ${v}`,
    actualizarA: (v: string) => `Actualizar a la ${v}`,
    queTrae: 'Qué trae',
    otraVersion: 'Otra versión…',
    esperandoAvance: 'el appliance la recibió…',
    titulo: 'Actualizaciones',
    enLaVersion: (v: string) => `en la ${v}`,
    versionNoReportada: 'no reportó su versión',
    ventana: 'Ventana de mantenimiento',
    cualquierDia: 'cualquier día',
    horaDelAppliance: (cuando: string) => `${cuando} (hora del appliance)`,
    sinVentana: 'ninguna: se actualiza apenas se le pide',
    actualizarEste: 'Actualizar este appliance',
    cambiarVentana: 'Cambiar la ventana',
    yaHayUna: 'Ya hay una actualización en curso.',
    sinVersionesNuevas: 'No hay publicada una versión más nueva con paquete.',
    alDia: (v: string) => `Al día: la ${v} es la más nueva.`,
    nadaPedido: 'Todavía no se le pidió nada a este appliance.',
    loPedido: 'Lo que se le pidió',
    cuando: 'Cuándo',
    que: 'Qué',
    comoFue: 'Cómo terminó',
    ordenCancelada: 'Orden cancelada.',
    pedirBoton: 'Pedirle que se actualice',
    elijaVersion: 'Elija la versión.',
    pedida: 'Se pidió la actualización. Se aplica dentro de su ventana de mantenimiento.',
    queHace:
      'El appliance espera su ventana de mantenimiento, verifica la firma del paquete, respalda su base y su clave maestra, actualiza y vuelve atrás solo si queda mal. El monitoreo no se interrumpe mientras espera.',
    ventanaGuardada: 'Ventana de mantenimiento guardada.',
    mismaHora: 'La hora de inicio y la de fin no pueden ser la misma.',
    ventanaQue: 'Cuándo se deja actualizar este appliance, en su propia hora. Sin días elegidos, cualquier día.',
    dias: 'Días',
    desde: 'Desde',
    hasta: 'Hasta',
    quitarVentana: 'Quitar la ventana',
    diasCortos: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
    estados: { hecha: 'hecha', entregada: 'en curso', pendiente: 'esperando al appliance', fallo: 'falló' },
  },

  versiones: {
    titulo: 'Versiones',
    que: 'Qué se publicó y con qué anda cada appliance. Publicar no toca ninguno: actualizar se pide aparte.',
    publicar: 'Publicar una versión',
    listas: 'Listas para publicar',
    listasQue: 'Paquetes que ya están en el blob, armados y firmados, y todavía no están en el catálogo.',
    paqueteDe: (tam: string, cuando: string) => `paquete de ${tam} · armado ${cuando}`,
    publicarEsta: (v: string) => `Publicar la ${v}`,
    publicarTitulo: (v: string) => `Publicar la versión ${v}`,
    canalBeta: 'Beta: la reciben los appliances de prueba primero.',
    canalEstable: 'Estable: la reciben todos los de ese canal.',
    afuera: 'El paquete está en otro lado (avanzado): poner la dirección y la firma a mano',
    faltaPaquete: 'Falta la dirección del paquete.',
    notasDelPaquete: 'Las notas vienen con el paquete: revíselas y publique.',
    parque: 'Con qué anda cada uno',
    sinParque: 'Ningún appliance reportó su versión todavía.',
    catalogo: 'Catálogo',
    sinVersiones: 'Todavía no se publicó ninguna versión.',
    numero: 'Versión',
    canal: 'Canal',
    publicada: 'Publicada',
    notas: 'Notas',
    necesita: (v: string) => `necesita la ${v} o más`,
    sinPaquete: 'sin paquete: no se puede aplicar',
    retirada: 'retirada',
    // Los formatos no se traducen: OVA, VHDX y qcow2 se llaman igual en todos lados.
    imagen: 'Imagen de VM',
    sinImagen: '—',
    imagenDel: (f: string) => `Imagen construida el ${f}`,
    formatos: { ova: 'OVA', vhdx: 'VHDX', qcow2: 'qcow2' } as Record<string, string>,
    repartir: 'Repartir',
    retirar: 'Retirar',
    borrar: 'Borrar',
    armar: 'Armar imagen de VM',
    rearmar: 'Rearmar imagen de VM',
    armando: (paso: string) => `Armando la imagen: ${paso}… (unos 25 min)`,
    armandoOk: (v: string) => `Se está armando la imagen de la ${v}: en unos 25 minutos queda para bajar.`,
    armadoFallo: (m: string) => `El último armado falló: ${m}`,
    borrarTitulo: (v: string) => `¿Borrar la versión ${v}?`,
    borrarQue: 'Sale del catálogo y se borran del blob su paquete y sus imágenes de VM (OVA, VHDX, qcow2). No se puede deshacer.',
    borradaOk: (v: string) => `Se borró la versión ${v}.`,
    noBorrarEnUso: (n: number) => (n === 1 ? 'La usa 1 appliance: no se puede borrar' : `La usan ${n} appliances: no se puede borrar`),
    noBorrarNueva: 'Es la más nueva, la que se baja para instalar un cliente nuevo: publique otra antes de borrarla',
    retirarTitulo: (v: string) => `¿Retirar la versión ${v}?`,
    retirarQue: 'Deja de ofrecerse para actualizar. Los appliances que ya la tienen siguen andando con ella.',
    enAppliances: (n: number) => (n === 1 ? 'en 1 appliance' : `en ${n} appliances`),
    laTienen: (n: number) => (n === 1 ? 'appliance la tiene' : 'appliances la tienen'),
    verTodas: (n: number) => `Ver las ${n}`,
    verMenos: 'Ver menos',
    retiradaOk: (v: string) => `Se retiró la versión ${v}.`,
    publicadaOk: (v: string) => `Se publicó la versión ${v}.`,
    repartirTitulo: (v: string) => `Repartir la ${v}`,
    repartirBoton: 'Pedírselo a todo el canal',
    repartido: (pedidas: number, saltadas: number) =>
      `Se le pidió a ${pedidas} appliance(s); ${saltadas} quedaron afuera (ya la tienen, o el salto es muy grande).`,
    repartirQue: (canal: string, version: string) =>
      `Todos los appliances del canal ${canal} que no estén ya en la ${version} reciben la orden. Cada uno la aplica dentro de su ventana de mantenimiento, verifica la firma del paquete y vuelve atrás solo si queda mal.`,
    repartirNota: 'También se puede de a uno, desde la página de cada appliance.',
    formatoNumero: 'El número de versión se escribe así: 1.4.2.',
    faltaNotasEn: 'Escriba qué cambió, en inglés.',
    faltaNotasEs: 'Escriba qué cambió, en español: lo lee el cliente.',
    numeroEjemplo: '1.4.2',
    queCambioEn: 'Qué cambió (inglés)',
    queCambioEs: 'Qué cambió (español)',
    paquete: 'Dirección del paquete',
    paqueteAyuda: 'Un enlace de solo lectura. Sin esto la versión queda anotada nada más: no se puede repartir.',
    firma: 'Firma del paquete',
    firmaAyuda: 'El appliance la verifica antes de instalar nada.',
    minima: 'Versión mínima (opcional)',
    minimaAyuda: 'Desde cuál se puede actualizar directo a esta.',
    minimaEjemplo: '1.3.0',
  },

  red: {
    columnas: { equipo: 'Equipo', cliente: 'Cliente', estado: 'Estado', queFalla: 'Qué falla', direccion: 'Dirección', appliance: 'Appliance' },
    titulo: 'Red',
    que: 'Lo que monitorean los appliances que dieron consentimiento. Primero lo caído, que es lo que necesita a alguien.',
    sinConsentimiento: 'Todavía ningún cliente dio consentimiento. Pídale que lo active en Ajustes → Centro de control.',
    verYOperar: 'Ver y operar',
    soloVer: 'Solo ver',
    caidos: 'caídos ·',
    parciales: 'parciales ·',
    equipos: 'equipos',
    fotoVieja: (cuando: string) => `Foto de ${cuando} — el appliance está sin contacto`,
    actualizado: (cuando: string) => `Actualizado ${cuando}`,
    buscarEjemplo: 'Buscar por nombre o dirección',
    buscarEquipos: 'Buscar equipos',
    todaOrganizacion: 'Todas las organizaciones',
    necesitanAlguien: 'Necesitan a alguien',
    cuenta: (equipos: string, atencion: string) => `${equipos} equipos · ${atencion} necesitan a alguien`,
    nadaCaido: 'No hay nada caído ahora mismo en ningún cliente.',
    nadaCoincide: 'Ningún equipo coincide. Pruebe con otra organización o limpie la búsqueda.',
    equipo: 'Equipo',
    direccion: 'Dirección',
    cliente: 'Cliente',
    sitio: 'Sitio',
    desde: 'Desde',
    reconocida: 'reconocida',
    volverAlAppliance: '← Volver al appliance',
    kpi: {
      disponibilidad: 'Disponibilidad',
      disponibilidadQue: (arriba: string, medibles: string) => `${arriba} de ${medibles} equipos medibles arriba`,
      caidos: 'Caídos',
      parciales: 'Parciales',
      sinDatos: 'Sin datos',
      nuevas: 'Caídas nuevas',
      nuevasQue: 'en la última hora',
      cronicas: 'Crónicas',
      cronicasQue: 'caídas hace más de 24 h',
    },
    porCliente: 'Salud por cliente',
    porClienteQue: 'Clic en un cliente para ver solo sus equipos.',
    antiguedad: 'Cuánto llevan caídos',
    antiguedadQue: 'Las nuevas necesitan a alguien ya; las crónicas, una decisión.',
    queFalla: 'Qué está fallando',
    queFallaQue: 'Las pruebas que dejaron equipos caídos o parciales.',
    tramos: ['< 15 min', '15–60 min', '1–6 h', '6–24 h', '1–3 d', '> 3 d'],
    todoArriba: 'todo arriba',
    filtrandoPor: 'Filtrado por',
    quitarFiltro: 'Quitar',
    estados: { caidos: 'Necesitan atención', todos: 'Todos', arriba: 'Arriba', desconocido: 'Sin datos' },
    estadoEquipo: { caido: 'Caído', parcial: 'Parcial', arriba: 'Arriba', desconocido: 'Sin datos' },
    fuentes: 'De dónde sale',
    nadaQueGraficar: 'No hay nada fallando: no hay caídas que graficar.',
    sinContacto: 'Sin contacto',
    estasAdentro: 'Está adentro del KNOC360 del cliente. Todo lo que haga queda en la bitácora de ellos',
    ultimaVez: (cuando: string) => ` · visto ${cuando}`,
    noDejaVer: 'Este cliente no dejó que el Centro de control vea su red.',
    sinContactoAhora: 'El appliance está sin contacto: no se puede abrir su red ahora.',
    marco: (organizacion: string) => `${organizacion} · KNOC360`,
    cliente_: 'Cliente',
    nEquipos: (n: number) => `${n} equipo${n === 1 ? '' : 's'}`,
    nCaidos: (n: number) => `${n} caído${n === 1 ? '' : 's'}`,
  },

  bitacora: {
    titulo: 'Bitácora',
    que: 'Quién hizo qué en el Centro, y cuándo. En hora de Costa Rica.',
    accion: 'Acción',
    todasLasAcciones: 'Todas las acciones',
    nadaConEsaAccion: 'Nada con esa acción.',
    nadaTodavia: 'Todavía no hay nada anotado.',
    cuando: 'Cuándo',
    quien: 'Quién',
    detalle: 'Detalle',
  },

  clavesApi: {
    titulo: 'Claves de API',
    que: 'Con qué entra un sistema de un tercero a este Centro de Mando sin una persona: una clave vale como un usuario de su rol, en la API y en la puerta del NOC hacia cada appliance. Lo que puede hacer en cada red sigue siendo lo que ese cliente permitió en su appliance.',
    soloAdmin: 'Solo un administrador maneja las claves de API.',
    nueva: 'Clave de API nueva',
    crear: 'Crear',
    nombreEjemplo: 'Integración Acme',
    nombreQue: 'Quién o qué la usa: así aparece en la bitácora.',
    faltaNombre: 'Falta el nombre de la clave.',
    rol: 'Rol',
    queRol: {
      admin: 'Todo lo que hace un administrador, salvo manejar claves de API.',
      soporte: 'Opera: entra a las redes de los clientes, llega a sus equipos, suspende appliances y ve la bitácora.',
    } as Record<string, string>,
    vence: 'Vence',
    venceQue: 'Opcional. Pasada esa fecha la clave deja de servir sola.',
    venceFutura: 'La fecha de vencimiento tiene que ser futura.',
    noVence: 'nunca',
    estado: 'Estado',
    activa: 'Activa',
    apagada: 'Apagada',
    vencida: 'Vencida',
    prefijo: 'Clave',
    ultimoUso: 'Último uso',
    nunca: 'nunca',
    creada: 'Creada',
    claveDe: (n: string) => `Clave de API de ${n}`,
    clave: 'Clave de API',
    unaSolaVez: 'Se muestra una sola vez.',
    claveQue: 'Pásela por un medio seguro. Si se pierde, borre esta y cree otra.',
    comoSeUsa: 'Va en cada pedido como bearer token. En la puerta del NOC (noc.knoc360.com) el appliance se elige con una cabecera:',
    claveGuardada: 'Ya la copié',
    apagar: 'Apagar',
    encender: 'Encender',
    borrar: 'Borrar',
    apagarA: (n: string) => `Apagar ${n}`,
    apagarQue: 'Quien la use queda afuera en el acto. Se puede volver a encender.',
    borrarA: (n: string) => `Borrar ${n}`,
    borrarQue: 'Desaparece para siempre: quien la use va a necesitar una clave nueva. La bitácora conserva lo que hizo.',
    ninguna: 'Todavía no hay claves de API.',
    creadaOk: (n: string) => `Se creó ${n}.`,
    encendida: (n: string) => `${n} quedó encendida.`,
    apagadaOk: (n: string) => `${n} quedó apagada.`,
    borradaOk: (n: string) => `Se borró ${n}.`,
  },
  usuarios: {
    titulo: 'Usuarios',
    que: 'Quién entra a este Centro de control y con qué rol. Nadie se borra: se desactiva, y la bitácora sigue diciendo quién hizo qué.',
    soloAdmin: 'Solo un administrador maneja los usuarios.',
    nuevo: 'Usuario nuevo',
    crear: 'Crear',
    editarA: (n: string) => `Editar a ${n}`,
    correo: 'Correo',
    rol: 'Rol',
    estado: 'Estado',
    ultimoIngreso: 'Último ingreso',
    nunca: 'nunca',
    vos: 'usted',
    activo: 'Activo',
    inactivo: 'Inactivo',
    bloqueadoHasta: (c: string) => `Bloqueado hasta ${c}`,
    nombreEjemplo: 'Nombre y apellido',
    correoEjemplo: 'persona@kuantabridge.com',
    faltaNombre: 'Falta el nombre.',
    faltaCorreo: 'Ese correo no es válido.',
    queRol: {
      admin: 'Todo, incluidos los usuarios, revocar y borrar appliances.',
      soporte: 'Opera: entra a las redes de los clientes, suspende appliances y ve la bitácora.',
      ventas: 'Da de alta organizaciones y appliances, y emite licencias. No opera redes.',
    },
    claveNueva: 'Clave nueva',
    claveNuevaPara: (n: string) => `Clave nueva para ${n}`,
    claveNuevaQue: 'Se genera una clave temporal. La actual deja de servir y se cierran sus sesiones abiertas. Si estaba bloqueado, se desbloquea.',
    claveDe: (c: string) => `Clave temporal de ${c}`,
    claveTemporal: 'Clave temporal',
    unaSolaVez: 'Se muestra una sola vez.',
    claveQue: 'Pásela por un medio seguro; la persona la cambia desde su menú al entrar.',
    claveGuardada: 'Ya la copié',
    desbloquear: 'Desbloquear',
    desactivar: 'Desactivar',
    activar: 'Activar',
    desactivarA: (n: string) => `Desactivar a ${n}`,
    desactivarQue: 'No va a poder entrar, y si está adentro sale en el acto. Se puede volver a activar cuando se quiera.',
    ninguno: 'Todavía no hay usuarios.',
    creado: (n: string) => `Se creó a ${n}.`,
    guardado: (n: string) => `Se guardó a ${n}.`,
    desactivado: (n: string) => `Se desactivó a ${n}.`,
    activado: (n: string) => `Se activó a ${n}.`,
    desbloqueado: (n: string) => `Se desbloqueó a ${n}.`,
  },

  organizaciones: {
    titulo: 'Organizaciones',
    que: 'Quién compró cada appliance. Un appliance siempre es de una organización.',
    nueva: 'Organización nueva',
    editarA: (nombre: string) => `Editar ${nombre}`,
    faltaNombre: 'Póngale un nombre a la organización.',
    nombreEjemplo: 'Hotel Norte',
    contactoEjemplo: 'Nombre, correo o teléfono de quien contesta',
    notasEjemplo: 'Qué compraron, quién decide, lo que valga la pena recordar.',
    ninguna: 'Todavía no hay organizaciones. Cree la primera para poder dar de alta appliances.',
    creada: (n: string) => `Se creó ${n}.`,
    guardada: (n: string) => `Se guardó ${n}.`,
  },

  salud: {
    titulo: (horas: number) => `CPU, memoria y disco de las últimas ${horas} horas`,
    sinDatos: (horas: number) => `No reportó salud en las últimas ${horas} horas.`,
    pie: 'por ciento, cada 5 min',
  },

  estado: {
    esperandoAlta: 'Esperando el alta',
    sinContacto: (cuanto: string) => `Sin contacto hace ${cuanto}`,
    nuncaConecto: 'Nunca conectó',
    discoAl: (pc: number) => `Disco al ${pc} %`,
    motoresCaidos: (caidos: number, total: number) => `${caidos} de ${total} motores caídos`,
    enLinea: 'En línea',
  },

  noExiste: {
    titulo: 'Esta página no existe',
    que: 'El enlace puede estar viejo o mal copiado.',
    alTablero: 'Ir al tablero',
  },
};

export default es;
